import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView, useMotionValue, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic2, Users, Sliders, Headphones, CheckCircle, Volume2, Sparkles, ClipboardList, UserCheck, Zap, Languages, FileText, Play, Pause, Search, Wrench, Award, MessageSquare, Repeat, Heart, Radio, Scissors, Clock } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import ContactScoping from '../components/ContactScoping';
import Navbar from '../components/Navbar';
import { ProjectBriefOverlay, StudioAudioTestOverlay, CastingRequirementsOverlay, ScriptSampleOverlay, LiveSessionOverlay, TokenPackOverlay } from '../components/ProjectBriefDrawer';
import QuickConnectForm from '../components/QuickConnectForm';

/* ═══════════════════════════════════════════════════════════
   Custom Data Collection — Landing Page v3
   Exact same layout/patterns as main Voqals page.
   ═══════════════════════════════════════════════════════════ */

const scrollToId = (id: string) => {
  const container = document.getElementById('main-scroll-container');
  // Prefer mobile twin when visible (mobile-only sections use `${id}-mobile`).
  // If the mobile twin exists and is rendered (offsetParent !== null), scroll
  // to it instead so navigation works on mobile where the desktop section is
  // display:none.
  let target: HTMLElement | null = document.getElementById(`${id}-mobile`);
  if (!target || target.offsetParent === null) {
    target = document.getElementById(id);
  }
  if (!target) return;
  if (container) {
    let top = 0;
    let el: HTMLElement | null = target;
    while (el && el !== container) {
      top += el.offsetTop;
      el = el.offsetParent as HTMLElement | null;
    }
    container.scrollTo({ top, behavior: 'smooth' });
  } else {
    target.scrollIntoView({ behavior: 'smooth' });
  }
};

/* ──── Animated Counter Hook ──── */
function useCounter(end: number, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return { count, ref };
}

/* ──── What We Handle — 5 pillars shown in hero right column ──── */
const handleItems = [
  {
    id: 0,
    title: 'Studios',
    subtitle: 'Certified across 20+ Indian cities',
    icon: <Mic2 className="w-5 h-5" />,
    targetId: 'studios',
  },
  {
    id: 1,
    title: 'Casting',
    subtitle: '2,000+ voice talent, every profile',
    icon: <Users className="w-5 h-5" />,
    targetId: 'casting',
  },
  {
    id: 2,
    title: 'Scripting',
    subtitle: 'Prompts & scenario engineering',
    icon: <FileText className="w-5 h-5" />,
    targetId: 'scripting',
  },
  {
    id: 3,
    title: 'Recording',
    subtitle: 'Directed by expert voice directors',
    icon: <Headphones className="w-5 h-5" />,
    targetId: 'recording',
  },
  {
    id: 4,
    title: 'Post Production',
    subtitle: 'Mastered, QC\u2019d, SFT-ready',
    icon: <Sliders className="w-5 h-5" />,
    targetId: 'post-production',
  },
];

/* ──── What We Handle Visualization (vertical list for hero right column) ──── */
function WhatWeHandle({ fullWidth = false }: { fullWidth?: boolean }) {
  // Mobile (fullWidth) sizes are intentionally larger so the panel reads
  // comfortably on a 375px viewport when used as the hero peek widget.
  const headerLabelSize = fullWidth ? 'text-[12px]' : 'text-[clamp(9px,0.65vw,12px)]';
  const headerEndSize = fullWidth ? 'text-[11px]' : 'text-[clamp(8px,0.6vw,11px)]';
  const itemNumberSize = fullWidth ? 'text-[11px]' : 'text-[clamp(8px,0.6vw,11px)]';
  const itemTitleSize = fullWidth ? 'text-[16px]' : 'text-[clamp(12px,0.95vw,16px)]';
  const itemSubtitleSize = fullWidth ? 'text-[13px]' : 'text-[clamp(10px,0.72vw,13px)]';
  const footerSize = fullWidth ? 'text-[12px]' : 'text-[clamp(9px,0.65vw,12px)]';
  return (
    <div className={`relative w-full ${fullWidth ? '' : 'max-w-[clamp(320px,28vw,480px)] mx-auto'}`}>
      {/* Glass panel wrapper */}
      <div data-glass-panel className={`relative ${fullWidth ? 'rounded-none border-0 bg-[#0a0a0a]' : 'glass-panel rounded-[clamp(16px,1.4vw,24px)] border border-white/10 bg-[#0a0a0a]'} backdrop-blur-xl p-[clamp(20px,1.6vw,32px)] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden`}>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B36FF]/8 via-transparent to-[#56CFFC]/5 pointer-events-none" />

        {/* Header */}
        <div data-widget-header className="relative z-10 flex items-center justify-between mb-[clamp(16px,1.2vw,24px)] pb-[clamp(12px,0.9vw,18px)] border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#56CFFC]" />
            <span className={`${headerLabelSize} font-bold uppercase tracking-[0.15em] text-white/90`}>What We Handle</span>
          </div>
          <span className={`${headerEndSize} font-semibold uppercase tracking-[0.12em] text-[#56CFFC]`}>End-to-End</span>
        </div>

        {/* Items */}
        <div className="relative z-10 flex flex-col gap-[clamp(10px,0.85vw,18px)]">
          {handleItems.map((item, index) => (
            <motion.button
              key={item.id}
              type="button"
              onClick={() => scrollToId(item.targetId)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ x: 4 }}
              className="group relative flex items-center gap-[clamp(12px,1vw,18px)] text-left w-full"
            >
              {/* Number + icon column */}
              <div className="relative flex flex-col items-center shrink-0">
                <div className="relative w-[clamp(36px,2.8vw,48px)] h-[clamp(36px,2.8vw,48px)] rounded-full bg-gradient-to-br from-[#1B36FF]/30 to-[#56CFFC]/20 border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] group-hover:border-[#56CFFC] group-hover:shadow-[0_0_20px_rgba(86,207,252,0.3)] transition-all">
                  {item.icon}
                </div>
                {/* Connector line */}
                {index < handleItems.length - 1 && (
                  <div className="absolute top-[clamp(36px,2.8vw,48px)] left-1/2 -translate-x-1/2 w-[2px] h-[clamp(10px,0.85vw,18px)] bg-gradient-to-b from-[#56CFFC]/40 to-[#56CFFC]/10" />
                )}
              </div>
              {/* Label column */}
              <div className="flex-1 min-w-0 pb-[clamp(2px,0.2vw,4px)]">
                <div className="flex items-baseline gap-2">
                  <span className={`${itemNumberSize} font-bold text-[#56CFFC]/60 tabular-nums`}>0{index + 1}</span>
                  <p className={`font-display font-bold ${itemTitleSize} text-white leading-tight tracking-tight group-hover:text-white transition-colors truncate`}>{item.title}</p>
                </div>
                <p className={`${itemSubtitleSize} text-white/50 group-hover:text-white/70 transition-colors leading-snug mt-[clamp(1px,0.1vw,3px)] truncate`}>{item.subtitle}</p>
              </div>
              {/* Arrow indicator */}
              <ArrowRight className="w-[clamp(12px,0.85vw,16px)] h-[clamp(12px,0.85vw,16px)] text-white/20 group-hover:text-[#56CFFC] group-hover:translate-x-1 transition-all shrink-0" />
            </motion.button>
          ))}
        </div>

        {/* Footer tagline */}
        <div className="relative z-10 mt-[clamp(16px,1.2vw,24px)] pt-[clamp(12px,0.9vw,18px)] border-t border-white/10 flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#56CFFC] animate-pulse shadow-[0_0_10px_rgba(86,207,252,0.6)]" />
          <p className={`${footerSize} text-white/70 leading-tight`}>
            <span className="font-bold text-white">One team.</span> One invoice. Complete datasets.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ──── Services Marquee (like language marquee on main page) ──── */
const studioCities = ['Mumbai', 'Delhi NCR', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Kochi', 'Indore', 'Guwahati'];

/* ──── Studio Audio Compare Widget (Standard vs Voqals) ────
   Loads audio.wav + spectrogram.webp + config.json from public/deep-dive/{tab}
   Mirrors the DiagnosticModal playback pattern but embedded inline with only 2 tabs. */
type StudioTab = 'standard' | 'voqals';

const STUDIO_TAB_STYLE: Record<StudioTab, {
  title: string; shortTitle: string; badge: string;
  badgeColor: string; accentColor: string; accentGlow: string;
}> = {
  standard: {
    title: 'Standard Studio', shortTitle: 'Standard', badge: 'OKAY',
    badgeColor: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    accentColor: '#eab308', accentGlow: 'rgba(234,179,8,0.15)',
  },
  voqals: {
    title: 'Voqals Certified', shortTitle: 'Voqals', badge: 'PERFECT',
    badgeColor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    accentColor: '#56CFFC', accentGlow: 'rgba(86,207,252,0.15)',
  },
};

const STUDIO_TAB_ORDER: StudioTab[] = ['standard', 'voqals'];

interface StudioTabConfig {
  noise: string;
  reverb: string;
  sampleRate: string;
  bitDepth: string;
}

const defaultStudioConfig: StudioTabConfig = { noise: '\u2014', reverb: '\u2014', sampleRate: '\u2014', bitDepth: '\u2014' };

function StudioAudioCompare({ fullWidth = false }: { fullWidth?: boolean } = {}) {
  const [activeTab, setActiveTab] = useState<StudioTab>('voqals');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);
  const [configs, setConfigs] = useState<Record<StudioTab, StudioTabConfig>>({
    standard: defaultStudioConfig,
    voqals: defaultStudioConfig,
  });

  // Fetch config JSONs once on mount
  useEffect(() => {
    STUDIO_TAB_ORDER.forEach((tab) => {
      fetch(`/deep-dive/${tab}/config.json`)
        .then((r) => r.json())
        .then((data: StudioTabConfig) => setConfigs((prev) => ({ ...prev, [tab]: data })))
        .catch(() => {});
    });
  }, []);

  const style = STUDIO_TAB_STYLE[activeTab];
  const config = configs[activeTab];

  // Measure tab indicator position
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const tabIndex = STUDIO_TAB_ORDER.indexOf(activeTab);
      const btn = buttons[tabIndex];
      if (btn) {
        setIndicatorStyle({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
        setIndicatorReady(true);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(measure)));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeTab]);

  // Audio loading
  useEffect(() => {
    const audioExt = activeTab === 'voqals' ? 'wav' : 'mp3';
    const audio = new Audio(`/deep-dive/${activeTab}/audio.${audioExt}`);
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    return () => { audio.pause(); audio.src = ''; };
  }, [activeTab]);

  // Audio playback + progress tracking
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let raf: number;
    const tick = () => {
      if (audio.duration) setPlaybackProgress(audio.currentTime / audio.duration);
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) { audio.play().catch(() => {}); raf = requestAnimationFrame(tick); }
    else { audio.pause(); }
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  const togglePlay = () => {
    if (playbackProgress >= 1) {
      const a = audioRef.current;
      if (a) a.currentTime = 0;
      setPlaybackProgress(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleTabChange = (tab: StudioTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setIsPlaying(false);
    setPlaybackProgress(0);
  };

  const fmt = (s: number) => {
    if (!Number.isFinite(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  return (
    <div className={`relative w-full ${fullWidth ? '' : 'gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl'}`}>
      {/* Dynamic tab-colored glow */}
      <div
        className="absolute top-0 right-0 w-[250px] h-[250px] pointer-events-none transition-all duration-700 rounded-[inherit]"
        style={{ background: `radial-gradient(circle, ${style.accentGlow} 0%, transparent 70%)` }}
      />
      <div
        className={`relative ${fullWidth ? 'px-5 py-5' : 'px-[clamp(12px,0.95vw,18px)] py-[clamp(10px,0.85vw,16px)]'} flex flex-col gap-[clamp(5px,0.55vw,10px)] overflow-hidden ${fullWidth ? 'rounded-none' : 'rounded-[inherit]'}`}
        style={fullWidth ? { background: '#0a0a0a' } : { background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
      >
        {/* Header */}
        <div className="flex flex-col items-center text-center shrink-0">
          <h4 className={`${fullWidth ? 'text-[11px]' : 'text-[9px] md:text-[clamp(8px,0.55vw,10px)]'} font-bold uppercase tracking-[0.2em] text-white/70`}>The Proof</h4>
        </div>

        {/* Tab Switcher — also serves as the peek "header strip" */}
        <div data-widget-header ref={tabContainerRef} className="bg-white/[0.04] p-1 md:p-1.5 rounded-full flex items-center relative w-full shrink-0">
          {indicatorReady && (
            <motion.div
              className="absolute top-1 md:top-1.5 bottom-1 md:bottom-1.5 rounded-full"
              style={{
                background: `linear-gradient(135deg, ${style.accentColor}40 0%, ${style.accentColor}20 100%)`,
                border: `1px solid ${style.accentColor}30`,
                boxShadow: `0 0 20px ${style.accentColor}15`,
              }}
              animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          {STUDIO_TAB_ORDER.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`relative z-10 flex-1 py-2.5 md:py-[clamp(7px,0.6vw,10px)] rounded-full text-[10px] md:text-[clamp(9px,0.65vw,11px)] font-bold tracking-[0.06em] uppercase transition-colors duration-300 flex items-center justify-center gap-1.5 ${
                activeTab === tab ? 'text-white' : 'text-white/35 hover:text-white/55'
              }`}
            >
              <span className="hidden md:inline">{STUDIO_TAB_STYLE[tab].title}</span>
              <span className="md:hidden">{STUDIO_TAB_STYLE[tab].shortTitle}</span>
              <span className={`text-[8px] md:text-[clamp(6px,0.46vw,8px)] px-1.5 py-0.5 rounded-full border font-bold tracking-wider ${STUDIO_TAB_STYLE[tab].badgeColor}`}>
                {STUDIO_TAB_STYLE[tab].badge}
              </span>
            </button>
          ))}
        </div>

        {/* Spectrogram */}
        <div className="relative rounded-2xl md:rounded-[clamp(12px,1vw,18px)] overflow-hidden border border-white/10 bg-black shrink-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full aspect-[1.91/1]"
            >
              <img
                src={`/deep-dive/${activeTab}/spectrogram.webp`}
                alt={`${style.title} spectrogram`}
                loading="lazy"
                width="960"
                height="503"
                className="w-full h-full object-cover"
              />
              {playbackProgress > 0 && (
                <div
                  className="absolute top-0 bottom-0 w-[2px] z-10 pointer-events-none"
                  style={{
                    left: `${playbackProgress * 100}%`,
                    background: style.accentColor,
                    boxShadow: `0 0 12px ${style.accentColor}, 0 0 4px ${style.accentColor}`,
                  }}
                />
              )}
              <button
                onClick={togglePlay}
                className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer group/play"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                <div
                  className={`w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-300 border border-white/20 ${isPlaying ? 'opacity-0 group-hover/play:opacity-70' : 'opacity-80 hover:opacity-100'}`}
                  style={{ backgroundColor: `${style.accentColor}40` }}
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white fill-current" />
                  ) : (
                    <Play className="w-5 h-5 text-white fill-current ml-0.5" />
                  )}
                </div>
              </button>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Audio Controls */}
        <div className="flex items-center gap-3 px-1 shrink-0">
          <div className="flex-1 flex flex-col gap-0.5">
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-75"
                style={{ width: `${playbackProgress * 100}%`, backgroundColor: style.accentColor }}
              />
            </div>
            <div className="flex justify-between text-[10px] md:text-[9px] font-mono text-white/40">
              <span>{fmt(playbackProgress * duration)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </div>
          <button
            onClick={togglePlay}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center border border-white/15 text-white hover:bg-white/10 transition-all shrink-0"
            style={{ backgroundColor: `${style.accentColor}15` }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-3.5 h-3.5 fill-current" />
            ) : (
              <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
            )}
          </button>
        </div>

        {/* Spec Strip */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + '-specs'}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-1.5 shrink-0"
          >
            {[
              { label: 'Noise', value: config.noise },
              { label: 'Reverb', value: config.reverb },
              { label: 'Rate', value: config.sampleRate },
              { label: 'Depth', value: config.bitDepth },
            ].map((spec) => (
              <div key={spec.label} className="text-center py-1 md:py-1.5 px-1 rounded-lg bg-white/[0.03] border border-white/5">
                <div className="text-[8px] md:text-[clamp(6px,0.46vw,8px)] font-bold tracking-widest uppercase text-white/25 mb-0.5 leading-none">{spec.label}</div>
                <div className="text-[12px] md:text-[clamp(9px,0.68vw,12px)] font-mono font-bold text-white leading-none">{spec.value}</div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ──── Casting Board Widget ────
   Visual companion to "We Don't Just Scout Voices, We Cast Them".
   3 logical talent categories — Professional / General Population / Specialty.
   Each tab shows the category total + 4 representative personas. */
type CastingTab = 'pro' | 'general' | 'specialty';

const CASTING_TAB_ORDER: CastingTab[] = ['pro', 'general', 'specialty'];

const CASTING_TABS: Record<CastingTab, { title: string; shortTitle: string; total: string; accent: string; accentGlow: string; }> = {
  pro:       { title: 'Professional Voice Talents', shortTitle: 'Pro Talents', total: '1,000+', accent: '#56CFFC', accentGlow: 'rgba(86,207,252,0.15)' },
  general:   { title: 'General Population',         shortTitle: 'General',     total: '900+',   accent: '#7B9CFF', accentGlow: 'rgba(123,156,255,0.15)' },
  specialty: { title: 'Specialty',                  shortTitle: 'Specialty',   total: '100+',   accent: '#A07BFF', accentGlow: 'rgba(160,123,255,0.15)' },
};

type Persona = {
  title: string;
  detail: string;
  count: string;
};

const PERSONAS_BY_TAB: Record<CastingTab, Persona[]> = {
  pro: [
    { title: 'Warm \u0026 Natural', detail: 'Conversational, approachable delivery', count: '350+' },
    { title: 'Authoritative',        detail: 'Confident, commanding presence',         count: '250+' },
    { title: 'Youthful',             detail: 'Bright, energetic, under-30',            count: '200+' },
    { title: 'Expressive',           detail: 'Rich range, dramatic depth',             count: '200+' },
  ],
  general: [
    { title: 'Young Adults',    detail: '18\u201329, urban + tier-2',     count: '300+' },
    { title: 'Working Pros',    detail: '30\u201345, metro mid-career',   count: '250+' },
    { title: 'Tier-2 Speakers', detail: 'Native regional, rural',         count: '200+' },
    { title: 'Bilingual',       detail: 'Hinglish \u0026 code-switched',  count: '150+' },
  ],
  specialty: [
    { title: 'Children',          detail: 'Ages 6\u201314, parent-consented', count: '30+' },
    { title: 'Elderly',           detail: 'Ages 60\u201380+',                 count: '25+' },
    { title: 'Speech Disfluency', detail: 'Stutters, lisps, hesitations',     count: '20+' },
    { title: 'Heavy Accents',     detail: 'Strong regional dialects',         count: '25+' },
  ],
};

function CastingBoard() {
  const [activeTab, setActiveTab] = useState<CastingTab>('pro');
  const [userInteracted, setUserInteracted] = useState(false);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  const style = CASTING_TABS[activeTab];
  const personas = PERSONAS_BY_TAB[activeTab];

  // Auto-rotate every 4.5s until user clicks
  useEffect(() => {
    if (userInteracted) return;
    const id = setInterval(() => {
      setActiveTab((prev) => {
        const idx = CASTING_TAB_ORDER.indexOf(prev);
        return CASTING_TAB_ORDER[(idx + 1) % CASTING_TAB_ORDER.length];
      });
    }, 4500);
    return () => clearInterval(id);
  }, [userInteracted]);

  // Tab indicator measure
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const tabIndex = CASTING_TAB_ORDER.indexOf(activeTab);
      const btn = buttons[tabIndex];
      if (btn) {
        setIndicatorStyle({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
        setIndicatorReady(true);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(measure)));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeTab]);

  const handleTabChange = (tab: CastingTab) => {
    setUserInteracted(true);
    if (tab === activeTab) return;
    setActiveTab(tab);
  };

  return (
    <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative w-full">
      {/* Dynamic tab-colored glow */}
      <div
        className="absolute top-0 right-0 w-[250px] h-[250px] pointer-events-none transition-all duration-700 rounded-[inherit]"
        style={{ background: `radial-gradient(circle, ${style.accentGlow} 0%, transparent 70%)` }}
      />
      <div
        className="relative px-[clamp(14px,1.1vw,20px)] py-[clamp(14px,1.15vw,22px)] flex flex-col gap-[clamp(9px,0.75vw,14px)] overflow-hidden rounded-[inherit]"
        style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
      >
        {/* Header */}
        <div className="flex flex-col items-center text-center shrink-0">
          <h4 className="text-[9px] md:text-[clamp(8px,0.55vw,10px)] font-bold uppercase tracking-[0.2em] text-white/70">The Casting Board</h4>
        </div>

        {/* Tab Switcher */}
        <div ref={tabContainerRef} className="bg-white/[0.04] p-1 md:p-1.5 rounded-full flex items-center relative w-full shrink-0">
          {indicatorReady && (
            <motion.div
              className="absolute top-1 md:top-1.5 bottom-1 md:bottom-1.5 rounded-full"
              style={{
                background: `linear-gradient(135deg, ${style.accent}40 0%, ${style.accent}20 100%)`,
                border: `1px solid ${style.accent}30`,
                boxShadow: `0 0 20px ${style.accent}15`,
              }}
              animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          {CASTING_TAB_ORDER.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`relative z-10 flex-1 py-2.5 md:py-[clamp(7px,0.6vw,10px)] rounded-full text-[10px] md:text-[clamp(9px,0.65vw,11px)] font-bold tracking-[0.06em] uppercase transition-colors duration-300 flex items-center justify-center ${
                activeTab === tab ? 'text-white' : 'text-white/35 hover:text-white/55'
              }`}
            >
              <span className="hidden md:inline">{CASTING_TABS[tab].title}</span>
              <span className="md:hidden">{CASTING_TABS[tab].shortTitle}</span>
            </button>
          ))}
        </div>

        {/* Persona cards (2x2) */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-2 gap-[clamp(8px,0.72vw,13px)]"
            >
              {personas.map((p) => (
                <div
                  key={p.title}
                  className="relative rounded-[clamp(10px,0.85vw,14px)] border border-white/8 bg-white/[0.025] px-[clamp(11px,0.9vw,16px)] py-[clamp(11px,0.9vw,16px)] flex flex-col gap-[clamp(8px,0.65vw,12px)] overflow-hidden"
                >
                  {/* Top row: accent dot + count chip */}
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className="w-[clamp(8px,0.65vw,11px)] h-[clamp(8px,0.65vw,11px)] rounded-full shrink-0"
                      style={{
                        background: style.accent,
                        boxShadow: `0 0 10px ${style.accent}80, 0 0 18px ${style.accent}40`,
                      }}
                    />
                    <span
                      className="text-[9px] md:text-[clamp(7px,0.52vw,10px)] font-mono font-bold tracking-wider px-1.5 py-0.5 rounded-md border tabular-nums"
                      style={{ borderColor: `${style.accent}30`, background: `${style.accent}12`, color: style.accent }}
                    >
                      {p.count}
                    </span>
                  </div>

                  {/* Title + detail */}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <h5 className="font-display font-black text-[11px] md:text-[clamp(10px,0.78vw,14px)] text-white tracking-tight uppercase leading-tight truncate">
                      {p.title}
                    </h5>
                    <p className="text-[9px] md:text-[clamp(7px,0.55vw,10px)] text-white/45 leading-snug">
                      {p.detail}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Stats footer strip — category total + global stats */}
        <div className="grid grid-cols-3 gap-1.5 md:gap-1.5 shrink-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab + '-cat'}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="text-center py-1 md:py-1.5 px-1 rounded-lg border"
              style={{ background: `${style.accent}10`, borderColor: `${style.accent}25` }}
            >
              <div className="text-[8px] md:text-[clamp(6px,0.46vw,8px)] font-bold tracking-widest uppercase mb-0.5 leading-none" style={{ color: `${style.accent}` }}>
                In This Pool
              </div>
              <div className="text-[12px] md:text-[clamp(9px,0.68vw,12px)] font-mono font-bold text-white leading-none">{style.total}</div>
            </motion.div>
          </AnimatePresence>
          {[
            { label: 'Total Network', value: '2,000+' },
            { label: 'Languages', value: '12' },
          ].map((stat) => (
            <div key={stat.label} className="text-center py-1 md:py-1.5 px-1 rounded-lg bg-white/[0.03] border border-white/5">
              <div className="text-[8px] md:text-[clamp(6px,0.46vw,8px)] font-bold tracking-widest uppercase text-white/25 mb-0.5 leading-none">{stat.label}</div>
              <div className="text-[12px] md:text-[clamp(9px,0.68vw,12px)] font-mono font-bold text-white leading-none">{stat.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──── Hero Mobile Wrapper — scroll-driven peek → fullscreen ────
   Mirrors the Usp1MobileWrapper pattern from App.tsx.
   • Outer 200dvh container for scroll distance
   • Sticky inner 100dvh that holds heading + widget
   • Scroll progress 0→1 drives heading fade-out and widget grow */
const HeroMobileWrapper = ({ onOpenDrawer }: { onOpenDrawer: () => void }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  // Peek = the natural height of just the "What We Handle / End-to-End"
  // header strip (measured from the rendered widget). Until first measure
  // we use a sensible default that's roughly that strip height.
  const PEEK_FALLBACK = 96;
  const peekHRef = useRef(PEEK_FALLBACK);
  const naturalHRef = useRef(360);
  const widgetHeight = useMotionValue(PEEK_FALLBACK);

  // Measure the widget's natural (unconstrained) content height + the
  // header-strip height (the peek target). The peek shows ONLY the header.
  useLayoutEffect(() => {
    const measure = () => {
      const el = widgetWrapperRef.current;
      if (!el) return;
      const h = el.scrollHeight;
      if (h > 0) naturalHRef.current = h;
      // Peek = distance from the top of the widget wrapper to the bottom
      // of the header strip + a small breathing margin so the divider
      // line under the header is clearly visible inside the panel. The
      // first item ("Studios") still stays fully hidden because the
      // header has its own bottom margin separating it from item #1.
      const header = el.querySelector('[data-widget-header]') as HTMLElement | null;
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const peek = (headerRect.bottom - elRect.top) + 14;
        if (peek > 0) peekHRef.current = peek;
      }
      // Reapply current progress with the freshest measurements
      const p = Math.min(1, Math.max(0, scrollYProgress.get() / 0.8));
      widgetHeight.set(peekHRef.current + (naturalHRef.current - peekHRef.current) * p);
    };
    measure();
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
      const p = Math.min(1, Math.max(0, progress / 0.8));
      widgetHeight.set(peekHRef.current + (naturalHRef.current - peekHRef.current) * p);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chrome — margins / radius shrink as the widget opens
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  // Background depth-of-field
  const bgBlur = useTransform(scrollYProgress, [0, 0.6], [0, 6]);
  const bgFilter = useTransform(bgBlur, (v) => `blur(${v}px)`);
  // Content blur — applied to pill, paragraph, trust line, CTA. The
  // heading itself stays sharp regardless.
  const contentBlur = useTransform(scrollYProgress, [0, 0.5], [0, 8]);
  const contentFilter = useTransform(contentBlur, (v) => `blur(${v}px)`);

  return (
    <div ref={sectionRef} id="hero-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full relative overflow-hidden">
        {/* Background glow — blurs (not fades) as the widget opens */}
        <motion.div
          style={{ filter: bgFilter }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/5 blur-[120px] rounded-full pointer-events-none animate-pulse-slow"
        />

        {/* Heading block — anchored to viewport, vertically centered in the
            area above the peek widget. Does not scroll up; widget grows
            up over it from the bottom, hiding it progressively. */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-[var(--px-container)] pt-20 pb-[80px] pointer-events-auto">
          {/* Pill — blurs as widget opens */}
          <motion.div style={{ filter: contentFilter }} className="mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Looking for a Studio in India?
            </div>
          </motion.div>
          {/* Heading — STAYS SHARP. */}
          <h1 className="font-display text-[clamp(20px,6.34vw,56px)] font-black tracking-tighter leading-[1.05] mb-6 w-full uppercase px-4">
            YOU DON'T NEED A STUDIO.<br />
            <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">YOU NEED A DATA PARTNER.</span>
          </h1>
          {/* Paragraph + trust line + CTA group — all blur as widget opens */}
          <motion.div style={{ filter: contentFilter }} className="w-full flex flex-col items-center">
            <p className="text-white/90 text-[length:var(--text-body)] max-w-sm mb-6 leading-relaxed text-center">
              Most teams start hunting for studios in Mumbai, Bangalore, or Delhi — then discover they also need <span className="text-white font-bold">casting, scripting, direction, linguists, QC, and SFT-ready delivery</span>. We handle all of it, in one engagement, across <span className="text-white font-bold">20+ Indian cities</span>.
            </p>
            {/* Trust line — main-page hero pattern, centered text */}
            <div className="mb-6 w-full">
              <div className="flex items-center gap-3">
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
                <p className="text-[10px] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase text-center flex-1">
                  Built by{' '}
                  <Link
                    to="/audio-company-india/"
                    className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC] hover:text-white/80 transition-colors"
                  >
                    the team
                  </Link>{' '}
                  whose speech data trains AI used by{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">hundreds of millions</span>{' '}
                  worldwide.
                </p>
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0" />
              </div>
            </div>
            {/* CTA */}
            <button
              onClick={onOpenDrawer}
              className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-3 text-center whitespace-nowrap">
                Scope My Project in 48h
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </motion.div>
        </div>

        {/* Widget — anchored to BOTTOM, higher z-index so it overlays the
            content as it grows upward. Peek = header strip only.
            Outer wrapper provides absolute positioning because the inner
            .gradient-border sets position:relative (would override absolute). */}
        <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-auto">
          <motion.div
            ref={widgetWrapperRef}
            style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
            className="gradient-border overflow-hidden"
          >
            <div className="w-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
              <WhatWeHandle fullWidth />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

/* ──── Studios Mobile Wrapper — same scroll-driven peek → fullscreen pattern as HeroMobileWrapper ──── */
const STUDIO_STEPS_MOBILE = [
  { num: '01', icon: <Search className="w-4 h-4" />, title: 'Scout', desc: 'Hand-picked rooms across Indian cities.' },
  { num: '02', icon: <Wrench className="w-4 h-4" />, title: 'Rebuild', desc: 'Acoustic panels, floating floors, sealed HVAC.' },
  { num: '03', icon: <Zap className="w-4 h-4" />, title: 'Rewire', desc: 'Clean power, grounding, shielded signal.' },
  { num: '04', icon: <Award className="w-4 h-4" />, title: 'Certify', desc: 'Measured end-to-end. No badge until every target is hit.' },
];

const StudiosMobileWrapper = ({ onOpenForm }: { onOpenForm: () => void }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  const PEEK_FALLBACK = 110;
  const peekHRef = useRef(PEEK_FALLBACK);
  const naturalHRef = useRef(420);
  const widgetHeight = useMotionValue(PEEK_FALLBACK);

  // ── Measure widget heights (identical to HeroMobileWrapper) ──
  useLayoutEffect(() => {
    const measure = () => {
      const el = widgetWrapperRef.current;
      if (!el) return;
      // Add 30px buffer — spectrogram aspect-ratio can add height after
      // the image loads, so the initial scrollHeight underreports.
      const h = el.scrollHeight + 30;
      if (h > 0) naturalHRef.current = h;
      const header = el.querySelector('[data-widget-header]') as HTMLElement | null;
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const peek = (headerRect.bottom - elRect.top) + 14;
        if (peek > 0) peekHRef.current = peek;
      }
      const p = Math.min(1, Math.max(0, scrollYProgress.get() / 0.8));
      widgetHeight.set(peekHRef.current + (naturalHRef.current - peekHRef.current) * p);
    };
    measure();
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    // Re-measure when spectrogram images load (they change content height)
    const el = widgetWrapperRef.current;
    if (el) {
      el.querySelectorAll('img').forEach((img) => {
        if (!(img as HTMLImageElement).complete) {
          img.addEventListener('load', measure, { once: true });
        }
      });
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Scroll-driven progress (identical to HeroMobileWrapper) ──
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
      const p = Math.min(1, Math.max(0, progress / 0.8));
      widgetHeight.set(peekHRef.current + (naturalHRef.current - peekHRef.current) * p);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived motion values (identical to HeroMobileWrapper) ──
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  const bgBlur = useTransform(scrollYProgress, [0, 0.6], [0, 6]);
  const bgFilter = useTransform(bgBlur, (v) => `blur(${v}px)`);
  const contentBlur = useTransform(scrollYProgress, [0, 0.5], [0, 8]);
  const contentFilter = useTransform(contentBlur, (v) => `blur(${v}px)`);

  return (
    <div ref={sectionRef} id="studios-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full relative overflow-hidden">
        {/* Background glow */}
        <motion.div
          style={{ filter: bgFilter }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/8 to-[#56CFFC]/4 blur-[150px] rounded-full pointer-events-none"
        />

        {/* Content block — vertically centred above the peek widget */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-[var(--px-container)] pt-20 pb-[160px] pointer-events-auto">
          {/* Pill — blurred */}
          <motion.div style={{ filter: contentFilter }} className="mb-5">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Wrench className="w-4 h-4 text-[#56CFFC]" />
              <span>The Voqals Rebuild Process</span>
            </div>
          </motion.div>

          {/* Heading — STAYS SHARP */}
          <h2 className="font-display text-[clamp(20px,7.13vw,56px)] font-black tracking-tighter leading-[1.02] mb-5 w-full uppercase px-4">
            WE DON'T FIND STUDIOS.<br />
            <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">WE REBUILD THEM.</span>
          </h2>

          {/* Paragraph + steps + trust line + CTA — all blurred */}
          <motion.div style={{ filter: contentFilter }} className="w-full flex flex-col items-center">
            <p className="text-white/90 text-[length:var(--text-body)] max-w-sm mb-5 leading-relaxed text-center">
              99% of studios in India would fail an AI dataset audit. So we don't book rooms — we <span className="text-white font-bold">scout, gut, rewire, and certify</span> every space ourselves, to a spec measured in dBFS and RT60. Every studio in our network has been through all four steps.
            </p>

            {/* 2×2 step grid */}
            <div className="grid grid-cols-2 gap-2.5 w-full mb-5">
              {STUDIO_STEPS_MOBILE.map((step) => (
                <div key={step.num} className="flex items-start gap-2 p-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-left">
                  <div className="relative shrink-0 w-9 h-9 rounded-full bg-[#0A0A0A] border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                    {step.icon}
                    <span className="absolute -top-1 -right-1 text-[7px] font-black text-[#56CFFC]/60 bg-[#0A0A0A] px-1 rounded-full border border-[#56CFFC]/20 tabular-nums">
                      {step.num}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-black text-[12px] text-white tracking-tight uppercase leading-tight">{step.title}</h3>
                    <p className="text-[10px] text-white/50 leading-snug mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust line */}
            <div className="w-full">
              <div className="flex items-center gap-3">
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
                <p className="text-[10px] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase text-center flex-1">
                  Certified studios across India. Trusted by the{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">biggest AI teams</span>{' '}
                  in the world.
                </p>
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0" />
              </div>
            </div>
            {/* CTA */}
            <button
              onClick={onOpenForm}
              className="group relative w-full mt-4 px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-3 text-center whitespace-nowrap">
                Request a Studio Audio Test
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </motion.div>
        </div>

        {/* Widget — anchored bottom, peeks the tab switcher header,
            grows upward as user scrolls. Identical structure to hero. */}
        <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-auto">
          <motion.div
            ref={widgetWrapperRef}
            style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
            className="gradient-border overflow-hidden"
          >
            <div className="w-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
              <StudioAudioCompare fullWidth />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   PAGE COMPONENT
   ══════════════════════════════════════════════════════════ */
export default function CustomDataCollectionPage() {
  const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false);
  const [isAudioTestOpen, setIsAudioTestOpen] = useState(false);
  const [isCastingFormOpen, setIsCastingFormOpen] = useState(false);
  const [isScriptFormOpen, setIsScriptFormOpen] = useState(false);
  const [isSessionFormOpen, setIsSessionFormOpen] = useState(false);
  const [isTokenPackOpen, setIsTokenPackOpen] = useState(false);

  usePageMeta({
    title: 'Custom Indian Speech Data Collection | Studios, Casting & Post-Production | Voqals',
    description: 'Stop hunting for studios. Voqals is India\u2019s end-to-end speech data partner \u2014 20+ certified cities, 2,000+ talents, 12 languages, directed recording, SFT-ready delivery. Trusted by 5 of the world\u2019s biggest tech companies. Full scope in 48 hours.',
    canonicalUrl: 'https://voqals.com/custom-data-collection/',
    ogTitle: 'Looking for a Recording Studio in India? You Need a Data Partner. \u2014 Voqals',
    ogDescription: 'Stop hunting for studios. Voqals is India\u2019s end-to-end speech data partner \u2014 20+ certified cities, 2,000+ talents, 12 languages, directed recording, SFT-ready delivery. Full scope in 48 hours.',
    ogImage: 'https://voqals.com/og-image.png',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Custom Indian Speech Data Collection',
        serviceType: 'Speech Data Collection for AI Training',
        provider: { '@type': 'Organization', name: 'Voqals', url: 'https://voqals.com/' },
        areaServed: 'IN',
        description:
          'End-to-end custom speech data collection in India: scenario engineering, casting, certified-studio recording, post-production, and structured delivery for AI teams.',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://voqals.com/' },
          { '@type': 'ListItem', position: 2, name: 'Custom Data Collection', item: 'https://voqals.com/custom-data-collection/' },
        ],
      },
    ],
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Custom Indian Speech Data Collection',
      provider: { '@type': 'Organization', name: 'Voqals MediaTech Pvt. Ltd.', url: 'https://voqals.com/' },
      description: 'End-to-end custom Indian speech data collection. Certified recording studios across 20+ Indian cities, 2,000+ voice talent, directed sessions, and SFT-ready delivery. The only partner you need for custom Indian speech data.',
      url: 'https://voqals.com/custom-data-collection/',
      areaServed: [
        { '@type': 'City', name: 'Mumbai' },
        { '@type': 'City', name: 'Delhi' },
        { '@type': 'City', name: 'Bangalore' },
        { '@type': 'City', name: 'Hyderabad' },
        { '@type': 'City', name: 'Chennai' },
        { '@type': 'City', name: 'Pune' },
        { '@type': 'City', name: 'Kolkata' },
        { '@type': 'City', name: 'Ahmedabad' },
        { '@type': 'Country', name: 'India' },
      ],
      serviceType: 'Custom Indian Speech Data Collection Service',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'AI Speech Data Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Certified Recording Studios' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Voice Talent Casting' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Directed Recording Sessions' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Audio Post-Production & Mastering' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dataset Annotation & Delivery' } },
        ],
      },
      knowsAbout: ['recording studio India', 'AI speech data', 'voice recording studio Mumbai', 'speech data collection', 'Indian language datasets'],
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const container = document.getElementById('main-scroll-container');
    if (container) container.scrollTo(0, 0);
  }, []);

return (
    <div
      id="main-scroll-container"
      className="h-[100dvh] w-full overflow-x-hidden overflow-y-scroll overscroll-y-contain no-scrollbar bg-[#000000] text-[#EEEEEE] selection:bg-[#1B36FF]/40 font-sans relative flex flex-col"
    >
      {/* ── Fixed Background — same as main Voqals page ── */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        className="fixed top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full border-[40px] border-[#1B36FF]/20 pointer-events-none z-0"
      />
      <motion.div
        animate={{ y: [0, 30, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        className="fixed bottom-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full border-[80px] border-[#A4BDE5]/5 pointer-events-none z-0"
      />
      <motion.div
        animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="fixed top-[20%] left-[10%] w-64 h-64 bg-[#1B36FF]/10 rounded-full blur-[60px] pointer-events-none z-0"
      />
      <motion.div
        animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        className="fixed bottom-[30%] right-[15%] w-80 h-80 bg-[#56CFFC]/10 rounded-full blur-[80px] pointer-events-none z-0"
      />
      <div className="fixed inset-0 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none z-0" />

      {/* ════════════════════════ NAVIGATION (shared with main page) ════════════════════════ */}
      <Navbar activeNav="process" onScrollToId={scrollToId} />

      {/* ════════════════════════ HERO — MOBILE (peek → fullscreen widget) ════════════════════════ */}
      <HeroMobileWrapper onOpenDrawer={() => setIsProjectDrawerOpen(true)} />

      {/* ════════════════════════ HERO — DESKTOP ════════════════════════ */}
      <section id="hero" className="hidden md:flex md:min-h-[100dvh] w-full shrink-0 relative flex-col md:pt-24 z-20 overflow-hidden">
        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/5 blur-[120px] rounded-full pointer-events-none animate-pulse-slow" />

        <main className="flex-1 flex flex-col md:flex-row items-center justify-center max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] py-0 gap-8 md:gap-[var(--gap-hero)] pointer-events-auto relative z-10">
          {/* Left Column: Typography & CTAs */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left justify-center w-full md:w-1/2 max-w-none md:max-w-[clamp(440px,42vw,960px)] shrink-0 relative">
            {/* Pill */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mb-6 md:absolute md:-top-[clamp(48px,3.5vw,86px)] md:left-0 md:mb-0"
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                Looking for a Recording Studio in India?
              </div>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="font-display text-[clamp(28px,9.2vw,56px)] md:text-[clamp(36px,3.3vw,66px)] font-black tracking-tighter leading-[1.05] mb-6 md:mb-[clamp(12px,1.2vw,28px)] uppercase px-4 md:px-0"
            >
              YOU DON'T NEED A STUDIO.<br />
              <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">YOU NEED A DATA PARTNER.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full mb-7 md:mb-[clamp(12px,1.4vw,28px)] leading-relaxed text-center md:text-left"
            >
              Most teams start by hunting for studios in Mumbai, Bangalore, or Delhi — then discover they also need <span className="text-white font-bold">casting, scripting, direction, linguists, QC, and SFT-ready delivery</span>. We handle all of it, in one engagement, across <span className="text-white font-bold">20+ Indian cities</span>.
            </motion.p>
            {/* Trust Line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="mb-7 md:mb-[clamp(14px,1.6vw,32px)] w-full md:w-auto"
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
                <p className="text-[10px] md:text-[clamp(10px,0.65vw,16px)] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
                  Built by{' '}
                  <Link
                    to="/audio-company-india/"
                    className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC] hover:text-white/80 transition-colors"
                  >
                    the team
                  </Link>{' '}
                  whose speech data trains AI used by{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">hundreds of millions</span>{' '}
                  worldwide.
                </p>
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0 md:hidden" />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 md:gap-5 w-full md:w-auto"
            >
              <button
                onClick={() => setIsProjectDrawerOpen(true)}
                className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
              >
                <span className="relative z-10 flex items-center gap-3 text-center whitespace-nowrap">
                  Scope My Project in 48h
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <button
                onClick={() => scrollToId('studios')}
                className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,14px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap"
              >
                <span className="relative z-10 flex items-center gap-2 text-center whitespace-nowrap">See The Pipeline <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              </button>
            </motion.div>
          </div>
          {/* Right Column: Pipeline Visualization */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, rotateY: 10 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="w-full md:w-1/2 perspective-1000 md:translate-y-[25px]"
          >
            <WhatWeHandle />
          </motion.div>
        </main>

        {/* Services Marquee — like language marquee on main page */}
        <div className="relative z-20 w-full pt-3 pb-6 md:pb-8 py-6 overflow-hidden">
          <div className="flex w-max animate-marquee">
            {[1, 2].map((set) => (
              <div key={set} className="flex items-center">
                {studioCities.map((city, index) => (
                  <div key={`${set}-${index}`} className="flex items-center group px-8 md:px-[clamp(40px,2.5vw,95px)]">
                    <span className="text-[10px] md:text-[clamp(9px,0.65vw,14px)] font-display font-medium text-white/90 uppercase tracking-[0.3em] md:tracking-[0.4em] group-hover:text-white transition-colors duration-500 cursor-default">
                      {city}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════ STUDIOS SECTION ════════════════════════ */}
      <StudiosMobileWrapper onOpenForm={() => setIsAudioTestOpen(true)} />
      <section id="studios" className="hidden md:flex md:min-h-[100dvh] w-full shrink-0 relative flex-col items-center justify-start 2xl:justify-center px-[var(--px-container)] pt-[clamp(56px,6.5vw,140px)] pb-12 md:pb-12 overflow-hidden">
        {/* Soft background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/8 to-[#56CFFC]/4 blur-[150px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col">
          {/* ── Centered Top: Pill + Headline ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-4 md:mb-[clamp(6px,0.6vw,14px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Wrench className="w-4 h-4 text-[#56CFFC]" />
              <span>The Voqals Rebuild Process</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[clamp(32px,10vw,60px)] md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-6 md:mb-[clamp(14px,1.3vw,28px)] uppercase px-4 md:px-0"
          >
            WE DON'T FIND STUDIOS.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE REBUILD THEM.</span>
          </motion.h2>

          {/* ── Two-column: Copy + Steps (left) | Widget (right) ── */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[clamp(32px,3vw,56px)] items-center">
            {/* Left: intro paragraph + vertical 4-step list */}
            <div className="w-full md:w-[48%] flex flex-col">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-white/85 text-[clamp(12px,0.9vw,15px)] leading-relaxed mb-5 md:mb-[clamp(16px,1.3vw,26px)]"
              >
                99% of recording studios in India would fail an AI dataset audit — acoustically leaky, electrically noisy, built for podcasts and jingles, not token-level training data. So we don't book rooms. We <span className="text-white font-bold">scout, gut, rewire, and certify</span> every space ourselves, to a spec measured in dBFS and RT60, not vibes. Every studio in our network has been through all four steps.
              </motion.p>

              {/* Vertical 4-step list */}
              <div className="relative flex flex-col gap-[clamp(10px,0.9vw,18px)]">
                {/* Vertical connector line */}
                <div className="hidden md:block absolute left-[clamp(18px,1.4vw,24px)] top-[clamp(36px,2.8vw,48px)] bottom-[clamp(36px,2.8vw,48px)] w-[1px] bg-gradient-to-b from-transparent via-[#56CFFC]/20 to-transparent pointer-events-none" />

                {[
                  {
                    num: '01',
                    icon: <Search className="w-4 h-4" />,
                    title: 'Scout',
                    desc: 'Hand-picked rooms across Indian cities — chosen for raw acoustic potential.',
                  },
                  {
                    num: '02',
                    icon: <Wrench className="w-4 h-4" />,
                    title: 'Rebuild',
                    desc: 'Acoustic panels, floating floors, sealed HVAC, isolation treatment.',
                    metric: 'RT60 < 0.1s',
                  },
                  {
                    num: '03',
                    icon: <Zap className="w-4 h-4" />,
                    title: 'Rewire',
                    desc: 'Clean power, proper grounding, shielded signal chains.',
                    metric: 'Zero EMI',
                  },
                  {
                    num: '04',
                    icon: <Award className="w-4 h-4" />,
                    title: 'Certify',
                    desc: 'Measured end-to-end. No badge until every target is hit.',
                    metric: '< \u221260dB',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.num}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex items-start gap-[clamp(10px,0.85vw,16px)] group"
                  >
                    {/* Icon circle */}
                    <div className="relative shrink-0 w-[clamp(36px,2.8vw,48px)] h-[clamp(36px,2.8vw,48px)] rounded-full bg-[#0A0A0A] border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.15)] group-hover:border-[#56CFFC] group-hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all">
                      {step.icon}
                      <span className="absolute -top-1 -right-1 text-[8px] md:text-[clamp(7px,0.5vw,9px)] font-black text-[#56CFFC]/60 bg-[#0A0A0A] px-1 rounded-full border border-[#56CFFC]/20 tabular-nums">
                        {step.num}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-display font-black text-[clamp(14px,1vw,18px)] text-white tracking-tight uppercase">{step.title}</h3>
                        {step.metric && (
                          <span className="text-[9px] md:text-[clamp(7px,0.5vw,10px)] font-mono font-bold text-[#56CFFC] px-1.5 py-0.5 rounded-md bg-[#56CFFC]/10 border border-[#56CFFC]/20 tabular-nums">
                            {step.metric}
                          </span>
                        )}
                      </div>
                      <p className="text-[clamp(10px,0.72vw,13px)] text-white/50 leading-snug">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trust line footer */}
              <p className="text-[9px] md:text-[clamp(9px,0.58vw,12px)] tracking-[0.15em] font-semibold text-white/50 leading-snug uppercase mt-5 md:mt-[clamp(16px,1.3vw,26px)]">
                Certified studios across India. Trusted by the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">biggest AI teams</span>{' '}
                in the world.
              </p>

              {/* Section CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-start gap-3 mt-5 md:mt-[clamp(18px,1.5vw,28px)]"
              >
                <button
                  onClick={() => setIsAudioTestOpen(true)}
                  className="group relative w-full sm:w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Request a Studio Audio Test
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </motion.div>
            </div>

            {/* Right: The Proof audio comparison widget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="flex-1 min-w-0 w-full flex flex-col"
            >
              <StudioAudioCompare />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════ CASTING SECTION ════════════════════════ */}
      <section id="casting" className="md:min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-start 2xl:justify-center px-0 md:px-[var(--px-container)] pt-[clamp(56px,6.5vw,140px)] pb-12 md:pb-12 overflow-hidden">
        {/* Soft background glows (kept from old placeholder for visual contrast vs Studios) */}
        <div className="absolute top-1/3 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/6 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-[#56CFFC]/4 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col">
          {/* ── Centered Top: Pill + Headline ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-5 md:mb-[clamp(14px,1.15vw,22px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Users className="w-4 h-4 text-[#56CFFC]" />
              <span>End-to-End Casting</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[clamp(20px,6.93vw,52px)] md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-8 md:mb-[clamp(32px,2.8vw,56px)] uppercase px-4 md:px-0"
          >
            WE DON'T SCOUT VOICES.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE CAST PERSONAS.</span>
          </motion.h2>

          {/* ── Two-column: Copy + Steps (left) | Casting Board (right) ── */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[clamp(32px,3vw,56px)] items-center">
            {/* Left: intro paragraph + vertical 4-step list + trust line */}
            <div className="w-full md:w-[48%] flex flex-col">
              {/* Desktop paragraph */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="hidden md:block text-white/85 text-[clamp(12px,0.9vw,15px)] leading-relaxed mb-[clamp(20px,1.6vw,32px)]"
              >
                Booking a studio is the easy part. The voice inside the studio is what trains the model — and a wrong-cast talent contaminates an entire dataset. We run <span className="text-white font-bold">full casting cycles for every brief: 2,000+ talent across every major Indian city</span>, <span className="text-white font-bold">12 Indian languages</span>, screened by voice directors and staff linguists before anyone hits record.
              </motion.p>
              {/* Mobile paragraph — shorter */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="md:hidden text-white/85 text-[length:var(--text-body)] leading-relaxed mb-6 text-center"
              >
                The voice inside the studio trains the model — and a wrong cast contaminates the entire dataset. <span className="text-white font-bold">2,000+ talent, 12 languages</span>, screened by voice directors and linguists before anyone hits record.
              </motion.p>

              {/* Vertical 4-step list */}
              <div className="relative flex flex-col gap-[clamp(14px,1.15vw,22px)]">
                {/* Vertical connector line */}
                <div className="hidden md:block absolute left-[clamp(18px,1.4vw,24px)] top-[clamp(36px,2.8vw,48px)] bottom-[clamp(36px,2.8vw,48px)] w-[1px] bg-gradient-to-b from-transparent via-[#56CFFC]/20 to-transparent pointer-events-none" />

                {[
                  {
                    num: '01',
                    icon: <ClipboardList className="w-4 h-4" />,
                    title: 'Casting Plan',
                    desc: 'Profile the voice you need — language, dialect, age, gender, tone, emotional range.',
                    metric: '12 languages',
                  },
                  {
                    num: '02',
                    icon: <Search className="w-4 h-4" />,
                    title: 'Source',
                    desc: 'Curated network across Indian cities + open auditions when the brief demands it.',
                    metric: '2,000+ voices',
                  },
                  {
                    num: '03',
                    icon: <UserCheck className="w-4 h-4" />,
                    title: 'Multi-Phase Auditions',
                    desc: 'Multiple rounds of in-studio auditions screened by voice directors and linguists.',
                    metric: 'Directors + linguists',
                  },
                  {
                    num: '04',
                    icon: <Headphones className="w-4 h-4" />,
                    title: 'Talent Management',
                    desc: 'Schedules, contracts, retakes, on-set coordination — handled end-to-end.',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.num}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex items-start gap-[clamp(10px,0.85vw,16px)] group"
                  >
                    {/* Icon circle */}
                    <div className="relative shrink-0 w-[clamp(36px,2.8vw,48px)] h-[clamp(36px,2.8vw,48px)] rounded-full bg-[#0A0A0A] border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.15)] group-hover:border-[#56CFFC] group-hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all">
                      {step.icon}
                      <span className="absolute -top-1 -right-1 text-[8px] md:text-[clamp(7px,0.5vw,9px)] font-black text-[#56CFFC]/60 bg-[#0A0A0A] px-1 rounded-full border border-[#56CFFC]/20 tabular-nums">
                        {step.num}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <h3 className="font-display font-black text-[clamp(14px,1vw,18px)] text-white tracking-tight uppercase">{step.title}</h3>
                        {step.metric && (
                          <span className="text-[9px] md:text-[clamp(7px,0.5vw,10px)] font-mono font-bold text-[#56CFFC] px-1.5 py-0.5 rounded-md bg-[#56CFFC]/10 border border-[#56CFFC]/20 tabular-nums">
                            {step.metric}
                          </span>
                        )}
                      </div>
                      <p className="text-[clamp(10px,0.72vw,13px)] text-white/50 leading-snug">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trust line footer */}
              <p className="text-[9px] md:text-[clamp(9px,0.58vw,12px)] tracking-[0.15em] font-semibold text-white/50 leading-snug uppercase mt-6 md:mt-[clamp(22px,1.85vw,36px)]">
                Already cast <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">50+ talents</span> across <span className="text-white">12 languages</span> for conversational AI personas at <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">5 of the world's biggest tech companies</span>. Thousands of hours delivered.
              </p>

              {/* Section CTA */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-start gap-3 mt-5 md:mt-[clamp(18px,1.5vw,28px)]"
              >
                <button
                  onClick={() => setIsCastingFormOpen(true)}
                  className="group relative w-full sm:w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Request Talent Samples
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </motion.div>
            </div>

            {/* Right: The Casting Board widget (desktop only) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="hidden md:flex flex-1 min-w-0 w-full flex-col"
            >
              <CastingBoard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════ SCRIPTING SECTION ════════════════════════ */}
      <section id="scripting" className="md:min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-start 2xl:justify-center px-0 md:px-[var(--px-container)] pt-[clamp(56px,6.5vw,140px)] pb-12 md:pb-12 overflow-hidden">
        {/* Soft background glows */}
        <div className="absolute top-1/4 left-1/3 w-[50%] h-[50%] bg-[#56CFFC]/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[45%] h-[45%] bg-[#A07BFF]/4 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col items-center">
          {/* ── Centered Pill ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-5 md:mb-[clamp(14px,1.15vw,22px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Sparkles className="w-4 h-4 text-[#56CFFC]" />
              <span>Content Planning</span>
            </div>
          </motion.div>

          {/* ── Single-line Headline ── */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[clamp(20px,7.19vw,52px)] md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-6 md:mb-[clamp(20px,1.7vw,32px)] uppercase px-4 md:px-0"
          >
            WE DESIGN HOW<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">PEOPLE ACTUALLY TALK.</span>
          </motion.h2>

          {/* ── Centered Intro Paragraph ── */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-[length:var(--text-body)] md:text-[clamp(13px,0.95vw,17px)] leading-relaxed text-center max-w-[clamp(540px,52vw,820px)] mx-auto mb-8 md:mb-[clamp(28px,2.4vw,48px)]"
          >
            A dataset is only as good as its prompts. Generic scripts produce generic models — stiff, unnatural, context-blind. Our linguists build content plans around how people actually speak in India: <span className="text-white font-semibold">code-switched, disfluent, emotional, domain-specific</span>. 12 script archetypes, engineered for the failure modes your model will hit in the real world.
          </motion.p>

          {/* ── Script Types: Desktop 4×3 grid ── */}
          <div className="hidden md:grid w-full grid-cols-4 gap-[clamp(10px,0.85vw,16px)]">
            {[
              { icon: <Sparkles className="w-4 h-4" />,      title: 'Spontaneous Speech',      desc: 'Unscripted, naturally flowing talk' },
              { icon: <Mic2 className="w-4 h-4" />,          title: 'Solo Monologues',         desc: 'Single-speaker narratives & reads' },
              { icon: <MessageSquare className="w-4 h-4" />, title: 'Assistant ↔ User',        desc: 'Two-party AI interaction flows' },
              { icon: <Users className="w-4 h-4" />,         title: 'Multi-Speaker',           desc: 'Group chats with turn-taking' },
              { icon: <Languages className="w-4 h-4" />,     title: 'Foundational Prompts',    desc: 'Phonetically-balanced coverage sets' },
              { icon: <Repeat className="w-4 h-4" />,        title: 'Code-Switched Dialogue',  desc: 'Hinglish & cross-lingual mixing' },
              { icon: <Heart className="w-4 h-4" />,         title: 'Emotion-Specific',        desc: 'Happy, tense, sad, excited — on cue' },
              { icon: <Zap className="w-4 h-4" />,           title: 'Task-Oriented Commands',  desc: 'Intent-driven utterances & queries' },
              { icon: <FileText className="w-4 h-4" />,      title: 'Read Speech',             desc: 'Broadcast-clean scripted delivery' },
              { icon: <ClipboardList className="w-4 h-4" />, title: 'Domain-Specific',         desc: 'Healthcare, finance, retail, auto' },
              { icon: <Pause className="w-4 h-4" />,         title: 'Disfluency-Rich',         desc: 'Hesitations, restarts, fillers' },
              { icon: <Radio className="w-4 h-4" />,         title: 'Wake-Word & Triggers',    desc: 'Short activation & command clips' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className="group rounded-[clamp(12px,1vw,18px)] border border-white/8 bg-white/[0.025] px-[clamp(12px,0.95vw,18px)] py-[clamp(10px,0.85vw,16px)] flex items-start gap-[clamp(9px,0.75vw,14px)] hover:border-[#56CFFC]/50 hover:bg-white/[0.04] transition-all duration-300"
              >
                <div className="shrink-0 w-[clamp(30px,2.35vw,40px)] h-[clamp(30px,2.35vw,40px)] rounded-[clamp(8px,0.7vw,12px)] bg-[#56CFFC]/10 border border-[#56CFFC]/25 flex items-center justify-center text-[#56CFFC] group-hover:bg-[#56CFFC]/15 group-hover:shadow-[0_0_20px_rgba(86,207,252,0.2)] transition-all">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0 pt-[1px]">
                  <h3 className="font-display font-black text-[clamp(11px,0.82vw,14px)] text-white tracking-tight uppercase leading-tight">{item.title}</h3>
                  <p className="text-[clamp(9px,0.68vw,12px)] text-white/45 leading-snug mt-0.5">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Section CTAs — desktop */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="hidden md:flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 md:mt-[clamp(28px,2.4vw,44px)]"
          >
            <button
              onClick={() => setIsScriptFormOpen(true)}
              className="group relative w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-3">
                Get a Sample Script for Your Use Case
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </motion.div>

          {/* ── Script Types: Mobile — two-row marquee, opposite directions ── */}
          <div className="md:hidden w-screen -mx-[var(--px-container)] overflow-hidden relative flex flex-col gap-3">
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
            {/* Row 1 — scrolls left */}
            <div className="flex animate-marquee w-max gap-3">
              {[
                { icon: <Sparkles className="w-3.5 h-3.5" />, title: 'Spontaneous Speech' },
                { icon: <Mic2 className="w-3.5 h-3.5" />,     title: 'Solo Monologues' },
                { icon: <MessageSquare className="w-3.5 h-3.5" />, title: 'Assistant ↔ User' },
                { icon: <Users className="w-3.5 h-3.5" />,    title: 'Multi-Speaker' },
                { icon: <Languages className="w-3.5 h-3.5" />, title: 'Foundational Prompts' },
                { icon: <Repeat className="w-3.5 h-3.5" />,   title: 'Code-Switched' },
              ].concat([
                { icon: <Sparkles className="w-3.5 h-3.5" />, title: 'Spontaneous Speech' },
                { icon: <Mic2 className="w-3.5 h-3.5" />,     title: 'Solo Monologues' },
                { icon: <MessageSquare className="w-3.5 h-3.5" />, title: 'Assistant ↔ User' },
                { icon: <Users className="w-3.5 h-3.5" />,    title: 'Multi-Speaker' },
                { icon: <Languages className="w-3.5 h-3.5" />, title: 'Foundational Prompts' },
                { icon: <Repeat className="w-3.5 h-3.5" />,   title: 'Code-Switched' },
              ]).map((item, i) => (
                <div key={`r1-${i}`} className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-2xl border border-white/10 bg-white/[0.03]">
                  <span className="text-[#56CFFC]">{item.icon}</span>
                  <span className="text-[12px] font-bold tracking-wide uppercase text-white/80 whitespace-nowrap">{item.title}</span>
                </div>
              ))}
            </div>
            {/* Row 2 — scrolls right (reverse) */}
            <div className="flex animate-marquee-reverse w-max gap-3">
              {[
                { icon: <Heart className="w-3.5 h-3.5" />,    title: 'Emotion-Specific' },
                { icon: <Zap className="w-3.5 h-3.5" />,      title: 'Task-Oriented' },
                { icon: <FileText className="w-3.5 h-3.5" />, title: 'Read Speech' },
                { icon: <ClipboardList className="w-3.5 h-3.5" />, title: 'Domain-Specific' },
                { icon: <Pause className="w-3.5 h-3.5" />,    title: 'Disfluency-Rich' },
                { icon: <Radio className="w-3.5 h-3.5" />,    title: 'Wake-Word' },
              ].concat([
                { icon: <Heart className="w-3.5 h-3.5" />,    title: 'Emotion-Specific' },
                { icon: <Zap className="w-3.5 h-3.5" />,      title: 'Task-Oriented' },
                { icon: <FileText className="w-3.5 h-3.5" />, title: 'Read Speech' },
                { icon: <ClipboardList className="w-3.5 h-3.5" />, title: 'Domain-Specific' },
                { icon: <Pause className="w-3.5 h-3.5" />,    title: 'Disfluency-Rich' },
                { icon: <Radio className="w-3.5 h-3.5" />,    title: 'Wake-Word' },
              ]).map((item, i) => (
                <div key={`r2-${i}`} className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-2xl border border-white/10 bg-white/[0.03]">
                  <span className="text-[#56CFFC]">{item.icon}</span>
                  <span className="text-[12px] font-bold tracking-wide uppercase text-white/80 whitespace-nowrap">{item.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section CTAs — mobile */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="md:hidden flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 px-4"
          >
            <button
              onClick={() => setIsScriptFormOpen(true)}
              className="group relative w-full sm:w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-3">
                Get a Sample Script for Your Use Case
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════ RECORDING SECTION ════════════════════════ */}
      <section id="recording" className="md:min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-start 2xl:justify-center px-0 md:px-[var(--px-container)] pt-[clamp(56px,6.5vw,140px)] pb-12 md:pb-12 overflow-hidden">
        {/* Soft background glows */}
        <div className="absolute top-1/3 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/6 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-[#56CFFC]/4 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col">
          {/* ── Centered Top: Pill + Headline ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-5 md:mb-[clamp(14px,1.15vw,22px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Mic2 className="w-4 h-4 text-[#56CFFC]" />
              <span>In The Booth</span>
            </div>
          </motion.div>

          {/* Desktop heading */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:block font-display md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-[clamp(32px,2.8vw,56px)] uppercase"
          >
            WE DON'T PRESS RECORD.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE DIRECT EVERY TAKE.</span>
          </motion.h2>
          {/* Mobile heading — shorter, 2 lines */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden font-display text-[clamp(20px,6.84vw,52px)] font-black tracking-tighter leading-[1.02] text-center mb-8 uppercase px-4"
          >
            WE DON'T PRESS RECORD.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE DIRECT EVERY TAKE.</span>
          </motion.h2>

          {/* ── Two-column: Copy + Steps (left) | Session Inclusions (right) ── */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[clamp(32px,3vw,56px)] items-center">
            {/* Left: intro paragraph + vertical 4-step list + trust line */}
            <div className="w-full md:w-[48%] flex flex-col">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-white/85 text-[length:var(--text-body)] md:text-[clamp(12px,0.9vw,15px)] leading-relaxed mb-6 md:mb-[clamp(20px,1.6vw,32px)] text-center md:text-left"
              >
                Every session runs in a <span className="text-white font-bold">Voqals-certified booth</span>, supervised by a staff linguist, and directed for prosody, intent, and emotional target — not just clean reads. <span className="text-white font-bold">Dual-studio setups</span> eliminate bleed on multi-speaker sessions. Zero client ops: talent scheduling, contracts, retakes, and take logs are all on us.
              </motion.p>

              {/* Vertical 4-step list */}
              <div className="relative flex flex-col gap-[clamp(14px,1.15vw,22px)]">
                {/* Vertical connector line */}
                <div className="hidden md:block absolute left-[clamp(18px,1.4vw,24px)] top-[clamp(36px,2.8vw,48px)] bottom-[clamp(36px,2.8vw,48px)] w-[1px] bg-gradient-to-b from-transparent via-[#56CFFC]/20 to-transparent pointer-events-none" />

                {[
                  {
                    num: '01',
                    icon: <Award className="w-4 h-4" />,
                    title: 'Certified Booth',
                    desc: 'Noise floor, RT60, and electrical interference measured before a mic goes live.',
                    metric: 'RT60 < 0.1s',
                  },
                  {
                    num: '02',
                    icon: <UserCheck className="w-4 h-4" />,
                    title: 'Directed Performance',
                    desc: 'Every take coached for prosody, emotion, and target delivery — not just read.',
                    metric: 'Target prosody',
                  },
                  {
                    num: '03',
                    icon: <Languages className="w-4 h-4" />,
                    title: 'Linguist-Supervised',
                    desc: 'A staff linguist sits every session, verifying pronunciation, dialect, and script fidelity.',
                    metric: 'Per session',
                  },
                  {
                    num: '04',
                    icon: <Headphones className="w-4 h-4" />,
                    title: 'Handled End-to-End',
                    desc: 'Talent scheduling, contracts, retakes, take logs — all on us. You never chase a voice actor.',
                    metric: 'Zero client ops',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.num}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex items-start gap-[clamp(10px,0.85vw,16px)] group"
                  >
                    {/* Icon circle */}
                    <div className="relative shrink-0 w-[clamp(36px,2.8vw,48px)] h-[clamp(36px,2.8vw,48px)] rounded-full bg-[#0A0A0A] border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.15)] group-hover:border-[#56CFFC] group-hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all">
                      {step.icon}
                      <span className="absolute -top-1 -right-1 text-[8px] md:text-[clamp(7px,0.5vw,9px)] font-black text-[#56CFFC]/60 bg-[#0A0A0A] px-1 rounded-full border border-[#56CFFC]/20 tabular-nums">
                        {step.num}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <h3 className="font-display font-black text-[clamp(14px,1vw,18px)] text-white tracking-tight uppercase">{step.title}</h3>
                        {step.metric && (
                          <span className="text-[9px] md:text-[clamp(7px,0.5vw,10px)] font-mono font-bold text-[#56CFFC] px-1.5 py-0.5 rounded-md bg-[#56CFFC]/10 border border-[#56CFFC]/20 tabular-nums">
                            {step.metric}
                          </span>
                        )}
                      </div>
                      <p className="text-[clamp(10px,0.72vw,13px)] text-white/50 leading-snug">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trust line footer */}
              <p className="text-[9px] md:text-[clamp(9px,0.58vw,12px)] tracking-[0.15em] font-semibold text-white/50 leading-snug uppercase mt-6 md:mt-[clamp(22px,1.85vw,36px)]">
                Every take ships to the <span className="text-white">Voqals quality standard</span>. You never chase a voice actor. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">Nothing less</span>.
              </p>

              {/* Section CTA */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-start gap-3 mt-5 md:mt-[clamp(18px,1.5vw,28px)]"
              >
                <button
                  onClick={() => setIsSessionFormOpen(true)}
                  className="group relative w-full sm:w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Sit in on a Live Session (Remote)
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </motion.div>
            </div>

            {/* Right: Session Inclusions widget (desktop only) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="hidden md:flex flex-1 min-w-0 w-full flex-col"
            >
              <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative w-full">
                {/* Static cyan glow */}
                <div
                  className="absolute top-0 right-0 w-[250px] h-[250px] pointer-events-none rounded-[inherit]"
                  style={{ background: 'radial-gradient(circle, rgba(86,207,252,0.15) 0%, transparent 70%)' }}
                />
                <div
                  className="relative px-[clamp(14px,1.1vw,20px)] py-[clamp(14px,1.15vw,22px)] flex flex-col gap-[clamp(10px,0.85vw,16px)] overflow-hidden rounded-[inherit]"
                  style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
                >
                  {/* Header */}
                  <div className="flex flex-col items-center text-center shrink-0">
                    <h4 className="text-[9px] md:text-[clamp(8px,0.55vw,10px)] font-bold uppercase tracking-[0.2em] text-white/70">Every Session Includes</h4>
                  </div>

                  {/* Measured Acoustics block */}
                  <div className="flex flex-col gap-[clamp(7px,0.55vw,11px)]">
                    <div className="flex items-center gap-2">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#56CFFC]/30" />
                      <span className="text-[8px] md:text-[clamp(6px,0.46vw,9px)] font-bold tracking-[0.2em] uppercase text-[#56CFFC]">Measured Acoustics</span>
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#56CFFC]/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-[clamp(7px,0.6vw,11px)]">
                      {[
                        { label: 'Noise Floor', value: '< -60 dBFS' },
                        { label: 'RT60',        value: '< 0.1 s' },
                        { label: 'EMI / RFI',   value: 'Zero' },
                        { label: 'Dual Bleed',  value: '< -50 dB' },
                      ].map((spec) => (
                        <div
                          key={spec.label}
                          className="rounded-[clamp(10px,0.85vw,14px)] border border-white/8 bg-white/[0.025] px-[clamp(10px,0.85vw,15px)] py-[clamp(8px,0.7vw,12px)] flex flex-col gap-1"
                        >
                          <div className="text-[8px] md:text-[clamp(6px,0.48vw,9px)] font-bold tracking-widest uppercase text-white/35 leading-none">{spec.label}</div>
                          <div className="text-[12px] md:text-[clamp(10px,0.78vw,14px)] font-mono font-black text-white leading-none tabular-nums">{spec.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* On-Set Team & Ops block */}
                  <div className="flex flex-col gap-[clamp(7px,0.55vw,11px)]">
                    <div className="flex items-center gap-2">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#56CFFC]/30" />
                      <span className="text-[8px] md:text-[clamp(6px,0.46vw,9px)] font-bold tracking-[0.2em] uppercase text-[#56CFFC]">On-Set Team & Ops</span>
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#56CFFC]/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-[clamp(7px,0.6vw,11px)]">
                      {[
                        { icon: <UserCheck className="w-3 h-3" />,     label: 'Voice Director' },
                        { icon: <Languages className="w-3 h-3" />,     label: 'Staff Linguist' },
                        { icon: <ClipboardList className="w-3 h-3" />, label: 'Dedicated PM' },
                        { icon: <Headphones className="w-3 h-3" />,    label: 'Talent Scheduling' },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-[clamp(10px,0.85vw,14px)] border border-white/8 bg-white/[0.025] px-[clamp(10px,0.85vw,15px)] py-[clamp(8px,0.7vw,12px)] flex items-center gap-[clamp(7px,0.6vw,11px)]"
                        >
                          <div className="w-[clamp(22px,1.75vw,30px)] h-[clamp(22px,1.75vw,30px)] rounded-[clamp(6px,0.55vw,9px)] bg-[#56CFFC]/10 border border-[#56CFFC]/25 flex items-center justify-center text-[#56CFFC] shrink-0">
                            {item.icon}
                          </div>
                          <span className="font-display font-black text-[10px] md:text-[clamp(9px,0.72vw,13px)] text-white tracking-tight uppercase leading-tight">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════ POST PRODUCTION SECTION ════════════════════════ */}
      <section id="post-production" className="md:min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-start 2xl:justify-center px-0 md:px-[var(--px-container)] pt-[clamp(56px,6.5vw,140px)] pb-12 md:pb-12 overflow-hidden">
        {/* Soft background glows */}
        <div className="absolute top-1/3 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/6 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-[#56CFFC]/4 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col justify-center flex-1">
          {/* ── Centered Top: Pill + Headline ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-5 md:mb-[clamp(18px,1.65vw,28px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Sliders className="w-4 h-4 text-[#56CFFC]" />
              <span>Post Production</span>
            </div>
          </motion.div>

          {/* Desktop heading */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:block font-display md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-[clamp(36px,3.3vw,64px)] uppercase"
          >
            WE DON'T CLEAN RECORDINGS.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE ENGINEER TOKENS.</span>
          </motion.h2>
          {/* Mobile heading — shorter, 2 lines */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden font-display text-[clamp(18px,5.69vw,52px)] font-black tracking-tighter leading-[1.02] text-center mb-8 uppercase px-4"
          >
            WE DON'T CLEAN RECORDINGS.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">WE ENGINEER TOKENS.</span>
          </motion.h2>

          {/* ── Two-column: Copy + Steps (left) | Delivery Spec (right) ── */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[clamp(32px,3vw,56px)] items-center">
            {/* Left: intro paragraph + vertical 4-step list + trust line */}
            <div className="w-full md:w-[42%] flex flex-col">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-white/85 text-[length:var(--text-body)] md:text-[clamp(11px,0.82vw,14px)] leading-relaxed mb-6 md:mb-[clamp(24px,2vw,38px)] text-center md:text-left"
              >
                Even in a treated booth, artifacts happen. We slice every recording into <span className="text-white font-bold">utterance-level tokens</span> first, clean each one down to the waveform, then master to a single loudness and EQ target. The result: your model hears <span className="text-white font-bold">one consistent voice across 10,000 utterances</span>, not ten thousand mixes. Two QC rounds — one human, one software — before anything ships.
              </motion.p>

              {/* Vertical 4-step list */}
              <div className="relative flex flex-col gap-[clamp(18px,1.55vw,28px)]">
                {/* Vertical connector line */}
                <div className="hidden md:block absolute left-[clamp(18px,1.4vw,24px)] top-[clamp(36px,2.8vw,48px)] bottom-[clamp(36px,2.8vw,48px)] w-[1px] bg-gradient-to-b from-transparent via-[#56CFFC]/20 to-transparent pointer-events-none" />

                {[
                  {
                    num: '01',
                    icon: <Scissors className="w-4 h-4" />,
                    title: 'Utterance Tokenization',
                    desc: 'Zero-crossing cuts — one phrase, one file.',
                    metric: 'Zero-crossing cuts',
                  },
                  {
                    num: '02',
                    icon: <Volume2 className="w-4 h-4" />,
                    title: 'Artifact Cleanup',
                    desc: 'Clicks, plosives, breath, sibilance — surgically removed.',
                    metric: 'Per token',
                  },
                  {
                    num: '03',
                    icon: <Wrench className="w-4 h-4" />,
                    title: 'Per-Token Mastering',
                    desc: 'Same loudness, EQ, and peak ceiling across every token.',
                    metric: 'TTS-grade',
                  },
                  {
                    num: '04',
                    icon: <CheckCircle className="w-4 h-4" />,
                    title: 'Dual QC',
                    desc: 'Human ear-check, then software verifies LUFS and peaks.',
                    metric: '2 rounds',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.num}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex items-start gap-[clamp(10px,0.85vw,16px)] group"
                  >
                    {/* Icon circle */}
                    <div className="relative shrink-0 w-[clamp(36px,2.8vw,48px)] h-[clamp(36px,2.8vw,48px)] rounded-full bg-[#0A0A0A] border border-[#56CFFC]/40 flex items-center justify-center text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.15)] group-hover:border-[#56CFFC] group-hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all">
                      {step.icon}
                      <span className="absolute -top-1 -right-1 text-[8px] md:text-[clamp(7px,0.5vw,9px)] font-black text-[#56CFFC]/60 bg-[#0A0A0A] px-1 rounded-full border border-[#56CFFC]/20 tabular-nums">
                        {step.num}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <h3 className="font-display font-black text-[clamp(14px,1vw,18px)] text-white tracking-tight uppercase">{step.title}</h3>
                        {step.metric && (
                          <span className="text-[9px] md:text-[clamp(7px,0.5vw,10px)] font-mono font-bold text-[#56CFFC] px-1.5 py-0.5 rounded-md bg-[#56CFFC]/10 border border-[#56CFFC]/20 tabular-nums">
                            {step.metric}
                          </span>
                        )}
                      </div>
                      <p className="text-[clamp(10px,0.72vw,13px)] text-white/85 leading-snug">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Section CTA */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-start gap-3 mt-5 md:mt-[clamp(18px,1.5vw,28px)]"
              >
                <button
                  onClick={() => setIsTokenPackOpen(true)}
                  className="group relative w-full sm:w-auto px-[clamp(20px,1.7vw,36px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Request a Sample Token Pack
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </motion.div>

            </div>

            {/* Right: Delivery Specification widget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="hidden md:flex flex-1 min-w-0 w-full flex-col"
            >
              <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative w-full">
                {/* Static cyan glow */}
                <div
                  className="absolute top-0 right-0 w-[250px] h-[250px] pointer-events-none rounded-[inherit]"
                  style={{ background: 'radial-gradient(circle, rgba(86,207,252,0.15) 0%, transparent 70%)' }}
                />
                <div
                  className="relative px-[clamp(16px,1.3vw,24px)] py-[clamp(20px,1.65vw,30px)] flex flex-col gap-[clamp(16px,1.35vw,24px)] overflow-hidden rounded-[inherit]"
                  style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
                >
                  {/* Header */}
                  <div className="flex flex-col items-center text-center shrink-0">
                    <h4 className="text-[9px] md:text-[clamp(8px,0.56vw,11px)] font-bold uppercase tracking-[0.22em] text-white/70">Token Pipeline</h4>
                  </div>

                  {/* Per-Token Cleanup block */}
                  <div className="flex flex-col gap-[clamp(11px,0.9vw,16px)]">
                    <div className="flex items-center gap-2">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#56CFFC]/30" />
                      <span className="text-[8px] md:text-[clamp(6px,0.48vw,9px)] font-bold tracking-[0.22em] uppercase text-[#56CFFC]">Per-Token Cleanup</span>
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#56CFFC]/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-[clamp(8px,0.65vw,12px)]">
                      {[
                        { icon: <MessageSquare className="w-3.5 h-3.5" />, label: 'Mouth Clicks' },
                        { icon: <Zap className="w-3.5 h-3.5" />,           label: 'Plosives' },
                        { icon: <Mic2 className="w-3.5 h-3.5" />,          label: 'Breath Noise' },
                        { icon: <Radio className="w-3.5 h-3.5" />,         label: 'Sibilance' },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-[clamp(10px,0.85vw,15px)] border border-white/8 bg-white/[0.025] px-[clamp(11px,0.9vw,16px)] py-[clamp(9px,0.78vw,14px)] flex items-center gap-[clamp(8px,0.65vw,12px)]"
                        >
                          <div className="w-[clamp(25px,1.95vw,34px)] h-[clamp(25px,1.95vw,34px)] rounded-[clamp(7px,0.6vw,10px)] bg-[#56CFFC]/10 border border-[#56CFFC]/25 flex items-center justify-center text-[#56CFFC] shrink-0">
                            {item.icon}
                          </div>
                          <span className="font-display font-black text-[10px] md:text-[clamp(9px,0.74vw,13px)] text-white tracking-tight uppercase leading-tight">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Two QC Rounds block */}
                  <div className="flex flex-col gap-[clamp(11px,0.9vw,16px)]">
                    <div className="flex items-center gap-2">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#56CFFC]/30" />
                      <span className="text-[8px] md:text-[clamp(6px,0.48vw,9px)] font-bold tracking-[0.22em] uppercase text-[#56CFFC]">Two QC Rounds</span>
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#56CFFC]/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-[clamp(8px,0.65vw,12px)]">
                      {[
                        { icon: <Headphones className="w-3.5 h-3.5" />, title: 'Engineer Pass', body: 'Artifacts · Filenames · Boundaries' },
                        { icon: <Search className="w-3.5 h-3.5" />,     title: 'Software Pass', body: 'LUFS · Peaks · Clips · DC offset' },
                      ].map((pass) => (
                        <div
                          key={pass.title}
                          className="rounded-[clamp(10px,0.85vw,15px)] border border-white/8 bg-white/[0.025] px-[clamp(11px,0.9vw,16px)] py-[clamp(10px,0.82vw,15px)] flex items-start gap-[clamp(8px,0.65vw,12px)]"
                        >
                          <div className="w-[clamp(25px,1.95vw,34px)] h-[clamp(25px,1.95vw,34px)] rounded-[clamp(7px,0.6vw,10px)] bg-[#56CFFC]/10 border border-[#56CFFC]/25 flex items-center justify-center text-[#56CFFC] shrink-0">
                            {pass.icon}
                          </div>
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="font-display font-black text-[10px] md:text-[clamp(9px,0.74vw,13px)] text-white tracking-tight uppercase leading-tight">{pass.title}</span>
                            <span className="text-[8px] md:text-[clamp(6px,0.5vw,10px)] font-semibold tracking-wider uppercase text-white/45 leading-snug">{pass.body}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════ CTA SECTION ════════════════════════ */}
      <section id="cta" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-center px-0 md:px-[var(--px-container)] pt-[clamp(100px,8.5vw,140px)] pb-10 md:pb-12 overflow-hidden">
        {/* Background glows */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[600px] pointer-events-none"
          animate={{ scale: [1, 1.1, 1], opacity: [0.45, 0.75, 0.45] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ background: 'radial-gradient(ellipse, rgba(27,54,255,0.16) 0%, rgba(86,207,252,0.07) 40%, transparent 70%)' }}
        />
        <div className="absolute top-1/4 right-[12%] w-[36%] h-[36%] bg-[#56CFFC]/5 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 left-[12%] w-[36%] h-[36%] bg-[#1B36FF]/8 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] z-10 flex flex-col justify-center flex-1">
          {/* Pill */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-5 md:mb-[clamp(18px,1.65vw,28px)]"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              <Sparkles className="w-4 h-4 text-[#56CFFC]" />
              <span>Let's Build</span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[clamp(18px,5.52vw,52px)] md:text-[clamp(44px,4.4vw,88px)] font-black tracking-tighter leading-[1.02] text-center mb-6 md:mb-[clamp(22px,1.95vw,36px)] uppercase px-4 md:px-0"
          >
            SEND US A BRIEF.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pr-2">GET A FULL SCOPE IN 48 HOURS.</span>
          </motion.h2>

          {/* Subtitle — leads with social proof */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/85 text-[length:var(--text-body)] md:text-[clamp(13px,0.95vw,16px)] max-w-[clamp(540px,54vw,860px)] mx-auto leading-relaxed text-center mb-8 md:mb-[clamp(28px,2.4vw,44px)]"
          >
            Already trusted by <span className="text-white font-bold">5 of the world's biggest tech companies</span> across <span className="text-white font-bold">12 Indian languages</span> and thousands of delivered hours. Send us what your model needs — languages, hours, use case, timeline — and within <span className="text-white font-bold">48 hours</span> you get a complete scope, talent samples, and a transparent quote. No discovery-call runaround. No vague estimates. One partner, end-to-end.
          </motion.p>

          {/* "What you get in 48 hours" — three concrete deliverables */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="hidden md:block w-full max-w-[clamp(640px,72vw,1080px)] mx-auto mb-8 md:mb-[clamp(28px,2.4vw,44px)]"
          >
            <div className="flex items-center justify-center gap-3 mb-4 md:mb-[clamp(14px,1.15vw,22px)]">
              <div className="h-[1px] w-[clamp(20px,2.5vw,40px)] bg-gradient-to-r from-transparent to-[#56CFFC]/40" />
              <span className="text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.22em] uppercase text-[#56CFFC]">What lands in your inbox in 48 h</span>
              <div className="h-[1px] w-[clamp(20px,2.5vw,40px)] bg-gradient-to-l from-transparent to-[#56CFFC]/40" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-[clamp(12px,1vw,20px)]">
              {[
                {
                  icon: <ClipboardList className="w-4 h-4" />,
                  title: 'Custom Scope Doc',
                  desc: 'Languages, talent profiles, script structure, acoustic spec, and delivery format — written for your model.',
                },
                {
                  icon: <Clock className="w-4 h-4" />,
                  title: 'Production Calendar',
                  desc: 'Studio bookings, casting calls, recording dates, and QC milestones — sequenced end-to-end.',
                },
                {
                  icon: <Award className="w-4 h-4" />,
                  title: 'Transparent Quote',
                  desc: 'Per-hour or per-token pricing. No surprise line items. Sign and recording starts the following week.',
                },
              ].map((deliverable, i) => (
                <motion.div
                  key={deliverable.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-[clamp(14px,1.2vw,22px)] border border-white/10 bg-white/[0.025] backdrop-blur-md px-[clamp(16px,1.3vw,24px)] py-[clamp(16px,1.35vw,24px)] flex flex-col gap-[clamp(8px,0.7vw,12px)] text-left"
                >
                  <div className="flex items-center gap-[clamp(10px,0.85vw,16px)]">
                    <div className="w-[clamp(32px,2.5vw,44px)] h-[clamp(32px,2.5vw,44px)] rounded-[clamp(8px,0.7vw,12px)] bg-[#56CFFC]/10 border border-[#56CFFC]/25 flex items-center justify-center text-[#56CFFC] shrink-0">
                      {deliverable.icon}
                    </div>
                    <h3 className="font-display font-black text-[clamp(13px,0.95vw,17px)] text-white tracking-tight uppercase leading-tight">{deliverable.title}</h3>
                  </div>
                  <p className="hidden md:block text-[clamp(11px,0.78vw,14px)] text-white/55 leading-snug">{deliverable.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-5"
          >
            <button
              onClick={() => setIsProjectDrawerOpen(true)}
              className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-3">
                Send Your Brief
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            <Link
              to="/#why-india"
              className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,14px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-2">Why Voqals <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          </motion.div>

          {/* Quick connect — softer entry path beneath the primary CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex justify-center w-full mt-[clamp(20px,1.8vw,36px)]"
          >
            <QuickConnectForm source="Quick Connect — Custom Data Collection / Send Brief CTA" />
          </motion.div>

          {/* Trust line — desktop only */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35 }}
            className="hidden text-[clamp(9px,0.58vw,12px)] tracking-[0.15em] font-semibold text-white/45 uppercase text-center mt-[clamp(20px,1.65vw,28px)]"
          >
            Backed by <span className="text-white">Voqals MediaTech Pvt. Ltd.</span> — India's end-to-end speech data partner.
          </motion.p>
        </div>
      </section>

      {/* ════════════════════════ CONTACT + FOOTER ════════════════════════ */}
      <ContactScoping />

      {/* Project Brief Lightbox (shared with main page) */}
      <ProjectBriefOverlay isOpen={isProjectDrawerOpen} onClose={() => setIsProjectDrawerOpen(false)} />

      {/* Studio Audio Test Lightbox — Studios section CTA */}
      <StudioAudioTestOverlay isOpen={isAudioTestOpen} onClose={() => setIsAudioTestOpen(false)} />

      {/* Casting Requirements Lightbox — Casting section CTA */}
      <CastingRequirementsOverlay isOpen={isCastingFormOpen} onClose={() => setIsCastingFormOpen(false)} />

      {/* Script Sample Lightbox — Scripting section CTA */}
      <ScriptSampleOverlay isOpen={isScriptFormOpen} onClose={() => setIsScriptFormOpen(false)} />

      {/* Live Session Lightbox — Recording section CTA */}
      <LiveSessionOverlay isOpen={isSessionFormOpen} onClose={() => setIsSessionFormOpen(false)} />

      {/* Token Pack Lightbox — Post-Production section CTA */}
      <TokenPackOverlay isOpen={isTokenPackOpen} onClose={() => setIsTokenPackOpen(false)} />
    </div>
  );
}
